'use client'

import Image from 'next/image'
import Link from 'next/link'
import { createPortal } from 'react-dom'
import { motion } from 'motion/react'
import { Page } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getAltTag } from '@/utilities/getAltTag'
import { getSeasonalMedia } from '@/utilities/getSeasonalMedia'
import { Media } from '../Media'
import { TypedLocale } from 'payload'
import { cn } from '@/lib/utils'
import { Gift } from 'lucide-react'

export function Hero({
  title,
  subtitle,
  media,
  type,
  winter,
  summer,
  locale,
}: Page['hero'] & { locale: TypedLocale }) {
  const seasonalMedia = getSeasonalMedia(winter, summer, media)

  return (
    <div>
      <section className="hero-lcp relative h-screen w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 h-full min-h-screen">
          <Media
            resource={seasonalMedia}
            className="h-full min-h-screen w-full object-cover"
            priority
            loading="eager"
            placeholder={getMediaUrl(media)}
            pictureClassName="absolute inset-0 h-full min-h-screen w-full object-cover"
            videoClassName="absolute inset-0 h-full min-h-screen w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Welcome Text - Optimized for LCP */}
        <div className="bottom-62 absolute left-0 w-full text-center">
          <h2 className="text-shadow-lg/40 font-serif text-3xl font-light tracking-[0.15em] text-white md:text-4xl lg:text-5xl">
            {locale === 'de' ? 'HERZLICH WILLKOMMEN' : 'WELCOME'}
          </h2>
        </div>

        {/* Logo and Resort Name - Optimized for LCP */}
        <div className="text-shadow-lg/40 hero-lcp absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-center text-white">
          {/* Bergsporthotel - Immediate visibility for LCP */}
          <div className="critical-text mb-2 text-sm font-light uppercase tracking-[0.3em] opacity-100 md:text-base">
            Bergsporthotel
          </div>

          {/* Main Antonie heading - Immediate visibility for LCP */}
          <h1 className="critical-text mb-2 font-sans text-6xl font-light tracking-wide opacity-100 md:text-7xl lg:text-8xl">
            Antonie
          </h1>

          {/* Stars - Immediate visibility for LCP */}
          <div className="critical-text text-xl font-light tracking-widest opacity-100">***s</div>
        </div>
        {/* Mobile booking button - Immediate visibility */}
        <div className="absolute bottom-10 right-0 flex w-full flex-row justify-center gap-2 pt-4 opacity-100 md:hidden">
          <Link href={locale === 'de' ? '/buchen' : '/book'}>
            <div className="bg-antonie-red px-4 py-3 text-center font-serif text-white">
              {locale === 'de' ? 'Jetzt buchen' : 'Book now'}
            </div>
          </Link>
          <Link href={locale === 'de' ? '/kontakt' : '/contact'}>
            <div className="bg-antonie-green px-4 py-3 text-center font-serif text-black">
              {locale === 'de' ? 'Anfragen' : 'Contact'}
            </div>
          </Link>
        </div>

        {/* Action Buttons - Animated but not critical for LCP */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="absolute bottom-10 left-1/2 hidden -translate-x-1/2 gap-4 space-y-1 md:grid md:grid-cols-2"
        >
          {[
            {
              href: locale === 'de' ? '/kontakt' : '/contact',
              text: locale === 'de' ? 'Anfragen' : 'Contact',
              color: 'antonie-green',
            },
            {
              href: locale === 'de' ? '/buchen' : '/book',
              text: locale === 'de' ? 'Jetzt buchen' : 'Book now',
              color: 'antonie-red',
            },
          ].map((button) => (
            <Link key={button.href} href={button.href}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={cn(
                  'text-shadow-black text-shadow-lg/10 font-serif px-4 py-3 text-center font-medium',
                  button.color === 'antonie-red'
                    ? 'bg-antonie-red text-white'
                    : 'bg-antonie-green text-black',
                )}
              >
                <span className="text-lg uppercase tracking-wider">{button.text}</span>
              </motion.div>
            </Link>
          ))}
        </motion.div>

        {/* Scroll Down Indicator - Animated but not critical for LCP */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="bottom-42 absolute left-1/2 -translate-x-1/2 transform"
        >
          <button
            onClick={() => {
              const scrollDistance = window.innerHeight * 0.8
              window.scrollTo({
                top: scrollDistance,
                behavior: 'smooth',
              })
            }}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/70 transition-colors hover:bg-white/10"
            aria-label={locale === 'de' ? 'Nach unten scrollen' : 'Scroll down'}
            title={locale === 'de' ? 'Nach unten scrollen' : 'Scroll down'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-white"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </motion.div>
      </section>
      {/* Booking Advantage Button - Sticky, stays visible when scrolling */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="rotate-270 fixed -right-24 top-1/2 z-30 hidden -translate-y-1/2 transform md:flex"
      >
          <Link
            href={locale === 'de' ? '/buchungsvorteile' : '/booking-advantages'}
            className="group flex items-center text-black"
          >
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="text-shadow-lg/10 flex items-center border-2 border-antonie-green bg-white px-6 py-4 font-serif text-black"
          >
            <Gift className="mr-2 h-4 w-4" />
            <span className="text-lg uppercase tracking-wider">
              {locale === 'de' ? 'Buchungsvorteile' : 'Booking Benefits'}
            </span>
          </motion.div>
        </Link>
      </motion.div>
    </div>
  )
}
